import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Box,
  Typography,
  IconButton,
  Chip,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControlLabel,
  Switch,
  Divider,
} from "@mui/material";
import { Close, Add, ExpandMore } from "@mui/icons-material";
import { addService } from "../../redux/slices/applicationSlice";

const serviceTypes = [
  { value: "WEB", label: "Web Service" },
  { value: "API", label: "API Service" },
  { value: "DATABASE", label: "Database" },
  { value: "CACHE", label: "Cache (Redis/Memcached)" },
  { value: "WORKER", label: "Background Worker" },
  { value: "CUSTOM", label: "Custom" },
];

const healthCheckTypes = [
  { value: "NONE", label: "None" },
  { value: "HTTP", label: "HTTP" },
  { value: "TCP", label: "TCP" },
  { value: "COMMAND", label: "Command" },
];

const popularImages = [
  { image: "nginx:alpine", label: "Nginx", type: "WEB", port: 80 },
  { image: "node:18-alpine", label: "Node.js 18", type: "WEB", port: 3000 },
  { image: "node:20-alpine", label: "Node.js 20", type: "WEB", port: 3000 },
  { image: "python:3.11-slim", label: "Python 3.11", type: "WEB", port: 8000 },
  { image: "redis:alpine", label: "Redis", type: "CACHE", port: 6379 },
  {
    image: "postgres:15-alpine",
    label: "PostgreSQL 15",
    type: "DATABASE",
    port: 5432,
  },
  { image: "mysql:8", label: "MySQL 8", type: "DATABASE", port: 3306 },
  { image: "mongo:6", label: "MongoDB 6", type: "DATABASE", port: 27017 },
  { image: "rabbitmq:3-alpine", label: "RabbitMQ", type: "WORKER", port: 5672 },
  { image: "httpd:alpine", label: "Apache", type: "WEB", port: 80 },
];

// Shared accordion overrides — flat, bordered, no default MUI shadow
const accordionSx = {
  boxShadow: "none",
  border: "1px solid",
  borderColor: "divider",
  borderRadius: "8px !important",
  "&:before": { display: "none" },
  "&.Mui-expanded": { mt: 0 },
};
const accordionSummarySx = {
  minHeight: 44,
  px: 2,
  "& .MuiAccordionSummary-content": { my: 0 },
  "& .MuiAccordionSummary-content.Mui-expanded": { my: 0 },
};

// Small uppercase section label
const SectionLabel = ({ children }) => (
  <Typography
    variant="caption"
    fontWeight={600}
    color="text.secondary"
    sx={{
      display: "block",
      mb: 1,
      textTransform: "uppercase",
      letterSpacing: 0.5,
    }}
  >
    {children}
  </Typography>
);

const AddServiceDialog = ({ open, onClose, applicationId, onSuccess }) => {
  const dispatch = useDispatch();
  const { createLoading } = useSelector((state) => state.applications);
  const [localError, setLocalError] = useState(null);

  const [formData, setFormData] = useState({
    name: "",
    image: "",
    serviceType: "WEB",
    domain: "",
    port: "",
    containerPort: 80,
    autoStart: true,
    envVars: [],
    volumes: [],
    healthCheckType: "NONE",
    healthCheckPath: "/health",
    healthCheckInterval: 30,
    healthCheckTimeout: 10,
    healthCheckRetries: 3,
    cpuLimit: "",
    memoryLimit: "",
  });

  const [envVarKey, setEnvVarKey] = useState("");
  const [envVarValue, setEnvVarValue] = useState("");
  const [dbVars, setDbVars] = useState({
    MYSQL_ROOT_PASSWORD: "",
    MYSQL_DATABASE: "",
    MYSQL_USER: "",
    MYSQL_PASSWORD: "",
    POSTGRES_USER: "",
    POSTGRES_PASSWORD: "",
    POSTGRES_DB: "",
    REDIS_PASSWORD: "",
  });
  const [volumeSource, setVolumeSource] = useState("");
  const [volumeTarget, setVolumeTarget] = useState("");
  const [connectionInfo, setConnectionInfo] = useState(null);

  // ── Handlers ───────────────────────────────────────────────────────────────
  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (field === "image") {
      setDbVars({
        MYSQL_ROOT_PASSWORD: "",
        MYSQL_DATABASE: "",
        MYSQL_USER: "",
        MYSQL_PASSWORD: "",
        POSTGRES_USER: "",
        POSTGRES_PASSWORD: "",
        POSTGRES_DB: "",
        REDIS_PASSWORD: "",
      });
    }
  };

  const handleAddEnvVar = () => {
    if (envVarKey && envVarValue) {
      setFormData((prev) => ({
        ...prev,
        envVars: [...prev.envVars, { key: envVarKey, value: envVarValue }],
      }));
      setEnvVarKey("");
      setEnvVarValue("");
    }
  };

  const handleRemoveEnvVar = (index) =>
    setFormData((prev) => ({
      ...prev,
      envVars: prev.envVars.filter((_, i) => i !== index),
    }));

  const handleAddVolume = () => {
    if (volumeSource && volumeTarget) {
      setFormData((prev) => ({
        ...prev,
        volumes: [
          ...prev.volumes,
          { source: volumeSource, target: volumeTarget },
        ],
      }));
      setVolumeSource("");
      setVolumeTarget("");
    }
  };

  const handleRemoveVolume = (index) =>
    setFormData((prev) => ({
      ...prev,
      volumes: prev.volumes.filter((_, i) => i !== index),
    }));

  const handleQuickAddImage = (imageInfo) => {
    setFormData((prev) => ({
      ...prev,
      image: imageInfo.image,
      name: prev.name || imageInfo.label.toLowerCase().replace(/\s+/g, "-"),
      serviceType: imageInfo.type,
      containerPort: imageInfo.port,
    }));
  };

  // ── DB predicates ──────────────────────────────────────────────────────────
  const isMySQLImage = (img) => img && img.toLowerCase().startsWith("mysql");
  const isPostgresImage = (img) =>
    img &&
    (img.toLowerCase().startsWith("postgres") ||
      img.toLowerCase().startsWith("timescale"));
  const isRedisImage = (img) => img && img.toLowerCase().startsWith("redis");

  const dbVarsValid = () => {
    if (isMySQLImage(formData.image))
      return (
        dbVars.MYSQL_ROOT_PASSWORD &&
        dbVars.MYSQL_DATABASE &&
        dbVars.MYSQL_USER &&
        dbVars.MYSQL_PASSWORD
      );
    if (isPostgresImage(formData.image))
      return (
        dbVars.POSTGRES_USER && dbVars.POSTGRES_PASSWORD && dbVars.POSTGRES_DB
      );
    if (isRedisImage(formData.image)) return dbVars.REDIS_PASSWORD;
    return true;
  };

  const isValid = formData.name && formData.image && dbVarsValid();

  // ── Submit ─────────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    const serviceData = {
      name: formData.name,
      image: formData.image,
      serviceType: formData.serviceType,
      domain: formData.domain || undefined,
      externalPort: formData.port ? parseInt(formData.port) : undefined,
      internalPort: parseInt(formData.containerPort) || 80,
      autoStart: formData.autoStart,
    };

    // Build env vars as ordered array to preserve user's key order
    let envVarsArr = formData.envVars
      .filter((ev) => ev.key.trim())
      .map((ev) => ({ key: ev.key.trim(), value: ev.value }));
    if (isMySQLImage(formData.image)) {
      envVarsArr = [
        ...envVarsArr,
        { key: "MYSQL_ROOT_PASSWORD", value: dbVars.MYSQL_ROOT_PASSWORD },
        { key: "MYSQL_DATABASE", value: dbVars.MYSQL_DATABASE },
        { key: "MYSQL_USER", value: dbVars.MYSQL_USER },
        { key: "MYSQL_PASSWORD", value: dbVars.MYSQL_PASSWORD },
      ];
    }
    if (isPostgresImage(formData.image)) {
      envVarsArr = [
        ...envVarsArr,
        { key: "POSTGRES_USER", value: dbVars.POSTGRES_USER },
        { key: "POSTGRES_PASSWORD", value: dbVars.POSTGRES_PASSWORD },
        { key: "POSTGRES_DB", value: dbVars.POSTGRES_DB },
      ];
    }
    if (isRedisImage(formData.image)) {
      envVarsArr = [
        ...envVarsArr,
        { key: "REDIS_PASSWORD", value: dbVars.REDIS_PASSWORD },
      ];
    }

    if (envVarsArr.length > 0) serviceData.envVars = envVarsArr;
    if (formData.volumes.length > 0) serviceData.volumes = formData.volumes;
    if (formData.healthCheckType !== "NONE") {
      serviceData.healthCheckType = formData.healthCheckType;
      serviceData.healthCheckPath = formData.healthCheckPath;
      serviceData.healthInterval = parseInt(formData.healthCheckInterval);
      serviceData.healthTimeout = parseInt(formData.healthCheckTimeout);
      serviceData.healthRetries = parseInt(formData.healthCheckRetries);
    }
    if (formData.cpuLimit) serviceData.cpuLimit = parseFloat(formData.cpuLimit);
    if (formData.memoryLimit)
      serviceData.memoryLimitMb = parseInt(formData.memoryLimit);

    try {
      await dispatch(
        addService({ appId: applicationId, serviceData }),
      ).unwrap();

      if (onSuccess) onSuccess();

      const serviceName = formData.name;
      let info = null;
      if (isMySQLImage(formData.image))
        info = {
          type: "MySQL",
          rows: [
            { label: "Hostname (DB_HOST)", value: serviceName },
            { label: "Port (DB_PORT)", value: "3306" },
            { label: "User (DB_USER)", value: dbVars.MYSQL_USER },
            { label: "Password (DB_PASSWORD)", value: dbVars.MYSQL_PASSWORD },
            { label: "Database (DB_NAME)", value: dbVars.MYSQL_DATABASE },
          ],
        };
      else if (isPostgresImage(formData.image))
        info = {
          type: "PostgreSQL",
          rows: [
            { label: "Hostname (DB_HOST)", value: serviceName },
            { label: "Port (DB_PORT)", value: "5432" },
            { label: "User (DB_USER)", value: dbVars.POSTGRES_USER },
            {
              label: "Password (DB_PASSWORD)",
              value: dbVars.POSTGRES_PASSWORD,
            },
            { label: "Database (DB_NAME)", value: dbVars.POSTGRES_DB },
          ],
        };
      else if (isRedisImage(formData.image))
        info = {
          type: "Redis",
          rows: [
            { label: "Hostname (REDIS_HOST)", value: serviceName },
            { label: "Port (REDIS_PORT)", value: "6379" },
            {
              label: "Password (REDIS_PASSWORD)",
              value: dbVars.REDIS_PASSWORD,
            },
          ],
        };

      if (info) {
        setConnectionInfo(info);
      } else {
        handleClose();
        // if (onSuccess) onSuccess();
      }
    } catch (err) {
      setLocalError(err?.message || "Failed to add service");
      console.error("Failed to add service:", err);
    }
  };

  // ── Reset / Close ──────────────────────────────────────────────────────────
  const handleClose = () => {
    setLocalError(null);
    setFormData({
      name: "",
      image: "",
      serviceType: "WEB",
      domain: "",
      port: "",
      containerPort: 80,
      autoStart: true,
      envVars: [],
      volumes: [],
      healthCheckType: "NONE",
      healthCheckPath: "/health",
      healthCheckInterval: 30,
      healthCheckTimeout: 10,
      healthCheckRetries: 3,
      cpuLimit: "",
      memoryLimit: "",
    });
    setEnvVarKey("");
    setEnvVarValue("");
    setVolumeSource("");
    setVolumeTarget("");
    setDbVars({
      MYSQL_ROOT_PASSWORD: "",
      MYSQL_DATABASE: "",
      MYSQL_USER: "",
      MYSQL_PASSWORD: "",
      POSTGRES_USER: "",
      POSTGRES_PASSWORD: "",
      POSTGRES_DB: "",
      REDIS_PASSWORD: "",
    });
    setConnectionInfo(null);
    onClose();
  };

  // ── Inline add-row button ──────────────────────────────────────────────────
  const AddIconBtn = ({ onClick, disabled }) => (
    <IconButton
      size="small"
      onClick={onClick}
      disabled={disabled}
      color="primary"
      sx={{
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 1.5,
        width: 36,
        height: 36,
        flexShrink: 0,
      }}
    >
      <Add fontSize="small" />
    </IconButton>
  );

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      {/* ── Title ─────────────────────────────────────────────────────────── */}
      <DialogTitle
        sx={{
          px: 3,
          py: 1.75,
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="subtitle1" fontWeight={700}>
            Add Service
          </Typography>
          <IconButton
            onClick={handleClose}
            size="small"
            sx={{ color: "text.secondary" }}
          >
            <Close fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>

      {/* ── Content ───────────────────────────────────────────────────────── */}
      <DialogContent sx={{ px: 3, py: 2.5 }}>
        {/* {error && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
            {error}
          </Alert>
        )} */}
        {localError && (
          <Alert
            severity="error"
            sx={{ mb: 2, borderRadius: 2 }}
            onClose={() => setLocalError(null)}
          >
            {localError}
          </Alert>
        )}

        {/* Quick Add */}
        <Box sx={{ mb: 2.5 }}>
          <SectionLabel>Quick Add</SectionLabel>
          <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap" }}>
            {popularImages.map((img) => (
              <Chip
                key={img.image}
                label={img.label}
                onClick={() => handleQuickAddImage(img)}
                variant={formData.image === img.image ? "filled" : "outlined"}
                color={formData.image === img.image ? "primary" : "default"}
                clickable
                size="small"
                sx={{ fontSize: "0.75rem", height: 26 }}
              />
            ))}
          </Box>
        </Box>

        <Divider sx={{ mb: 2.5 }} />

        {/* Basic Info */}
        <Box sx={{ mb: 2.5 }}>
          <SectionLabel>Basic Info</SectionLabel>
          <Grid container spacing={1.5}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                size="small"
                label="Service Name *"
                value={formData.name}
                onChange={(e) => handleChange("name", e.target.value)}
                helperText="Unique name within the application"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Service Type</InputLabel>
                <Select
                  value={formData.serviceType}
                  label="Service Type"
                  onChange={(e) => handleChange("serviceType", e.target.value)}
                >
                  {serviceTypes.map((st) => (
                    <MenuItem key={st.value} value={st.value}>
                      {st.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                size="small"
                label="Docker Image *"
                value={formData.image}
                onChange={(e) => handleChange("image", e.target.value)}
                placeholder="nginx:alpine"
                helperText="Docker Hub or private registry image"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                size="small"
                label="Domain (optional)"
                value={formData.domain}
                onChange={(e) => handleChange("domain", e.target.value)}
                placeholder="api.example.com"
                helperText="Public domain for Traefik routing"
              />
            </Grid>
            <Grid size={{ xs: 6, sm: 3 }}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Host Port"
                value={formData.port}
                onChange={(e) => handleChange("port", e.target.value)}
                placeholder="Auto"
                helperText="Empty = auto"
              />
            </Grid>
            <Grid size={{ xs: 6, sm: 3 }}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Container Port"
                value={formData.containerPort}
                onChange={(e) => handleChange("containerPort", e.target.value)}
              />
            </Grid>
          </Grid>
        </Box>

        {/* DB Required Vars */}
        {(isMySQLImage(formData.image) ||
          isPostgresImage(formData.image) ||
          isRedisImage(formData.image)) && (
          <Box
            sx={{
              mb: 2.5,
              p: 2,
              border: "1px solid",
              borderColor: "warning.light",
              borderRadius: 2,
              bgcolor: (t) =>
                t.palette.mode === "dark" ? "rgba(251,191,36,0.06)" : "#fffbeb",
            }}
          >
            <SectionLabel>
              {isMySQLImage(formData.image)
                ? "MySQL Required Variables"
                : isPostgresImage(formData.image)
                  ? "PostgreSQL Required Variables"
                  : "Redis Required Variables"}
            </SectionLabel>

            {isMySQLImage(formData.image) && (
              <Grid container spacing={1.5}>
                {[
                  ["MYSQL_ROOT_PASSWORD", "Root Password"],
                  ["MYSQL_DATABASE", "Database Name"],
                  ["MYSQL_USER", "User"],
                  ["MYSQL_PASSWORD", "Password"],
                ].map(([key, label]) => (
                  <Grid key={key} size={{ xs: 6, sm: 3 }}>
                    <TextField
                      fullWidth
                      size="small"
                      label={label}
                      value={dbVars[key]}
                      onChange={(e) =>
                        setDbVars((v) => ({ ...v, [key]: e.target.value }))
                      }
                      required
                      type={key.includes("PASSWORD") ? "password" : "text"}
                    />
                  </Grid>
                ))}
              </Grid>
            )}

            {isPostgresImage(formData.image) && (
              <Grid container spacing={1.5}>
                {[
                  ["POSTGRES_USER", "User"],
                  ["POSTGRES_PASSWORD", "Password"],
                  ["POSTGRES_DB", "Database Name"],
                ].map(([key, label]) => (
                  <Grid key={key} size={{ xs: 12, sm: 4 }}>
                    <TextField
                      fullWidth
                      size="small"
                      label={label}
                      value={dbVars[key]}
                      onChange={(e) =>
                        setDbVars((v) => ({ ...v, [key]: e.target.value }))
                      }
                      required
                      type={key.includes("PASSWORD") ? "password" : "text"}
                    />
                  </Grid>
                ))}
              </Grid>
            )}

            {isRedisImage(formData.image) && (
              <Grid container spacing={1.5}>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Password"
                    type="password"
                    value={dbVars.REDIS_PASSWORD}
                    onChange={(e) =>
                      setDbVars((v) => ({
                        ...v,
                        REDIS_PASSWORD: e.target.value,
                      }))
                    }
                    required
                  />
                </Grid>
              </Grid>
            )}
          </Box>
        )}

        {/* Accordions */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mb: 2 }}>
          {/* Environment Variables */}
          <Accordion sx={accordionSx} disableGutters>
            <AccordionSummary
              expandIcon={<ExpandMore fontSize="small" />}
              sx={accordionSummarySx}
            >
              <Typography variant="body2" fontWeight={500}>
                Environment Variables
                {formData.envVars.length > 0 && (
                  <Chip
                    label={formData.envVars.length}
                    size="small"
                    sx={{ ml: 1, height: 18, fontSize: "0.7rem" }}
                  />
                )}
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ px: 2, pt: 1, pb: 2 }}>
              <Box
                sx={{ display: "flex", gap: 1, mb: 1.5, alignItems: "center" }}
              >
                <TextField
                  size="small"
                  label="Key"
                  value={envVarKey}
                  onChange={(e) => setEnvVarKey(e.target.value)}
                  sx={{ flex: 1 }}
                />
                <TextField
                  size="small"
                  label="Value"
                  value={envVarValue}
                  onChange={(e) => setEnvVarValue(e.target.value)}
                  sx={{ flex: 1 }}
                />
                <AddIconBtn
                  onClick={handleAddEnvVar}
                  disabled={!envVarKey || !envVarValue}
                />
              </Box>
              {formData.envVars.length > 0 && (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                  {formData.envVars.map((ev, i) => (
                    <Chip
                      key={i}
                      label={`${ev.key}=${ev.value.length > 12 ? ev.value.substring(0, 12) + "…" : ev.value}`}
                      onDelete={() => handleRemoveEnvVar(i)}
                      size="small"
                      sx={{
                        fontFamily: "monospace",
                        fontSize: "0.7rem",
                        height: 22,
                      }}
                    />
                  ))}
                </Box>
              )}
            </AccordionDetails>
          </Accordion>

          {/* Volumes */}
          <Accordion sx={accordionSx} disableGutters>
            <AccordionSummary
              expandIcon={<ExpandMore fontSize="small" />}
              sx={accordionSummarySx}
            >
              <Typography variant="body2" fontWeight={500}>
                Volumes
                {formData.volumes.length > 0 && (
                  <Chip
                    label={formData.volumes.length}
                    size="small"
                    sx={{ ml: 1, height: 18, fontSize: "0.7rem" }}
                  />
                )}
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ px: 2, pt: 1, pb: 2 }}>
              <Box
                sx={{ display: "flex", gap: 1, mb: 1.5, alignItems: "center" }}
              >
                <TextField
                  size="small"
                  label="Host Path / Volume Name"
                  value={volumeSource}
                  onChange={(e) => setVolumeSource(e.target.value)}
                  placeholder="/data or volume-name"
                  sx={{ flex: 1 }}
                />
                <TextField
                  size="small"
                  label="Container Path"
                  value={volumeTarget}
                  onChange={(e) => setVolumeTarget(e.target.value)}
                  placeholder="/var/lib/data"
                  sx={{ flex: 1 }}
                />
                <AddIconBtn
                  onClick={handleAddVolume}
                  disabled={!volumeSource || !volumeTarget}
                />
              </Box>
              {formData.volumes.length > 0 && (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                  {formData.volumes.map((vol, i) => (
                    <Chip
                      key={i}
                      label={`${vol.source}:${vol.target}`}
                      onDelete={() => handleRemoveVolume(i)}
                      size="small"
                      sx={{
                        fontFamily: "monospace",
                        fontSize: "0.7rem",
                        height: 22,
                      }}
                    />
                  ))}
                </Box>
              )}
            </AccordionDetails>
          </Accordion>

          {/* Health Check */}
          <Accordion sx={accordionSx} disableGutters>
            <AccordionSummary
              expandIcon={<ExpandMore fontSize="small" />}
              sx={accordionSummarySx}
            >
              <Typography variant="body2" fontWeight={500}>
                Health Check
                {formData.healthCheckType !== "NONE" && (
                  <Chip
                    label={formData.healthCheckType}
                    size="small"
                    color="success"
                    sx={{ ml: 1, height: 18, fontSize: "0.7rem" }}
                  />
                )}
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ px: 2, pt: 1, pb: 2 }}>
              <Grid container spacing={1.5}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Type</InputLabel>
                    <Select
                      value={formData.healthCheckType}
                      label="Type"
                      onChange={(e) =>
                        handleChange("healthCheckType", e.target.value)
                      }
                    >
                      {healthCheckTypes.map((hc) => (
                        <MenuItem key={hc.value} value={hc.value}>
                          {hc.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                {formData.healthCheckType === "HTTP" && (
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Path"
                      value={formData.healthCheckPath}
                      onChange={(e) =>
                        handleChange("healthCheckPath", e.target.value)
                      }
                      placeholder="/health"
                    />
                  </Grid>
                )}
                {formData.healthCheckType !== "NONE" && (
                  <>
                    <Grid size={{ xs: 4 }}>
                      <TextField
                        fullWidth
                        size="small"
                        type="number"
                        label="Interval (s)"
                        value={formData.healthCheckInterval}
                        onChange={(e) =>
                          handleChange("healthCheckInterval", e.target.value)
                        }
                      />
                    </Grid>
                    <Grid size={{ xs: 4 }}>
                      <TextField
                        fullWidth
                        size="small"
                        type="number"
                        label="Timeout (s)"
                        value={formData.healthCheckTimeout}
                        onChange={(e) =>
                          handleChange("healthCheckTimeout", e.target.value)
                        }
                      />
                    </Grid>
                    <Grid size={{ xs: 4 }}>
                      <TextField
                        fullWidth
                        size="small"
                        type="number"
                        label="Retries"
                        value={formData.healthCheckRetries}
                        onChange={(e) =>
                          handleChange("healthCheckRetries", e.target.value)
                        }
                      />
                    </Grid>
                  </>
                )}
              </Grid>
            </AccordionDetails>
          </Accordion>

          {/* Resource Limits */}
          <Accordion sx={accordionSx} disableGutters>
            <AccordionSummary
              expandIcon={<ExpandMore fontSize="small" />}
              sx={accordionSummarySx}
            >
              <Typography variant="body2" fontWeight={500}>
                Resource Limits
                {(formData.cpuLimit || formData.memoryLimit) && (
                  <Chip
                    label={[
                      formData.cpuLimit && `${formData.cpuLimit} CPU`,
                      formData.memoryLimit && `${formData.memoryLimit} MB`,
                    ]
                      .filter(Boolean)
                      .join(" · ")}
                    size="small"
                    sx={{ ml: 1, height: 18, fontSize: "0.7rem" }}
                  />
                )}
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ px: 2, pt: 1, pb: 2 }}>
              <Grid container spacing={1.5}>
                <Grid size={{ xs: 6 }}>
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    label="CPU Limit (cores)"
                    value={formData.cpuLimit}
                    onChange={(e) => handleChange("cpuLimit", e.target.value)}
                    placeholder="e.g. 0.5"
                    helperText="0.1 – 4 cores"
                    inputProps={{ step: 0.1, min: 0.1, max: 4 }}
                  />
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    label="Memory Limit (MB)"
                    value={formData.memoryLimit}
                    onChange={(e) =>
                      handleChange("memoryLimit", e.target.value)
                    }
                    placeholder="e.g. 512"
                    helperText="64 – 8192 MB"
                    inputProps={{ min: 64, max: 8192 }}
                  />
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
        </Box>

        {/* Auto Start */}
        <Box
          sx={{
            px: 1.5,
            py: 0.25,
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 2,
          }}
        >
          <FormControlLabel
            control={
              <Switch
                checked={formData.autoStart}
                onChange={(e) => handleChange("autoStart", e.target.checked)}
                size="small"
              />
            }
            label={
              <Typography variant="body2" fontWeight={500}>
                Start service automatically after creation
              </Typography>
            }
          />
        </Box>

        {/* Connection Info (shown after DB/cache create success) */}
        {connectionInfo && (
          <Box
            sx={{
              mt: 2,
              p: 2,
              border: "2px solid",
              borderColor: "success.main",
              borderRadius: 2,
            }}
          >
            <Typography
              variant="subtitle2"
              fontWeight={700}
              color="success.main"
              gutterBottom
            >
              ✓ {connectionInfo.type} service created
            </Typography>
            <Alert severity="warning" sx={{ mb: 1.5, borderRadius: 1.5 }}>
              <Typography variant="body2" fontWeight={600}>
                Add these as env vars in your <strong>app service</strong> — use
                whatever key names your code expects.
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                The <strong>hostname is the service name</strong>.{" "}
                <code>localhost</code> will not work between containers.
              </Typography>
            </Alert>
            <Box
              sx={{
                fontFamily: "monospace",
                fontSize: "0.8rem",
                bgcolor: "grey.900",
                color: "grey.100",
                p: 1.5,
                borderRadius: 1.5,
              }}
            >
              {connectionInfo.rows.map((r) => (
                <Box key={r.label} sx={{ display: "flex", gap: 2, mb: 0.5 }}>
                  <Box sx={{ color: "grey.400", minWidth: 220 }}>{r.label}</Box>
                  <Box sx={{ color: "#86efac", fontWeight: 700 }}>
                    {r.value}
                  </Box>
                </Box>
              ))}
            </Box>
            <Button
              variant="contained"
              color="success"
              size="small"
              disableElevation
              sx={{
                mt: 1.5,
                textTransform: "none",
                fontWeight: 600,
                borderRadius: 2,
              }}
              onClick={() => {
                handleClose();
                if (onSuccess) onSuccess();
              }}
            >
              Got it — Close
            </Button>
          </Box>
        )}
      </DialogContent>

      {/* ── Actions ───────────────────────────────────────────────────────── */}
      <DialogActions
        sx={{
          px: 3,
          py: 2,
          borderTop: "1px solid",
          borderColor: "divider",
          gap: 1,
        }}
      >
        <Button
          onClick={handleClose}
          sx={{ textTransform: "none", fontWeight: 600 }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!isValid || createLoading || !!connectionInfo}
          disableElevation
          sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2 }}
        >
          {createLoading ? "Adding..." : "Add Service"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddServiceDialog;
